import { cx } from "@linaria/core";
import { fullWidth, useBlock } from "timvir/core";
import { useResizeObserver, useResizeObserverEntry } from "timvir/hooks";
import * as React from "react";
import { Caption, Handle, Ruler } from "./internal";

/**
 * The underlying DOM element which is rendered by this component.
 */
const Root = "div";
interface Props extends React.ComponentPropsWithoutRef<typeof Root> {
  /**
   * URL that should be loaded in the viewport. Can be absolute or relative.
   */
  src: string;

  /**
   * TODO: Document
   */
  code?: string;
}
function Viewport(props: Props, ref: React.ForwardedRef<React.ElementRef<typeof Root>>) {
  const block = useBlock(props);
  const {
    src,
    code,
    className,
    ...rest
  } = block.props;

  /*
   * The container measures the width of the main column. It is used to initialize
   * the default width.
   */
  const [containerRef, containerROE] = useResizeObserverEntry();

  /*
   * The SVG spans across the full width. It is used to restrict the max width.
   */
  const [svgRef, svgROE] = useResizeObserverEntry();
  const [height, setHeight] = React.useState<undefined | number>(undefined);
  const [maxHeight, setMaxHeight] = React.useState<undefined | number>(undefined);
  const [width, setWidth] = React.useState<undefined | number>(undefined);
  React.useEffect(() => {
    if (width === undefined && svgROE) {
      setWidth(svgROE.contentRect.width);
    } else if (svgROE) {
      const max = svgROE.contentRect.width - 2 * (56 + 8 + 8);
      if (width !== undefined && width > max) {
        setWidth(max);
      }
    }
  }, [containerROE, svgROE, setWidth, width]);
  const lock = React.useRef("");
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  React.useEffect(() => {
    const onMouseMove = (ev: MouseEvent) => {
      if (lock.current && svgROE) {
        ev.preventDefault();
        const max = svgROE.contentRect.width - 2 * (56 + 8 + 8);
        setWidth(width => Math.min(max, Math.max(320, (width ?? 0) + 2 * ev.movementX * ({
          left: -1,
          right: 1
        }[lock.current] ?? 1))));
      }
    };
    const onMouseUp = () => {
      lock.current = "";
      iframeRef.current!.style.userSelect = "";
      iframeRef.current!.style.pointerEvents = "";
      setHeight(height => {
        setMaxHeight(height);
        return height;
      });
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [svgROE]);
  const iframeRO = useResizeObserver(entries => {
    const height = entries[entries.length - 1].contentRect.height;
    setHeight(height);
    setMaxHeight(Math.max(height, maxHeight ?? 0));
  });

  /*
   * Note this useEffect runs on each render. This is fine beause it doesn't do any
   * expensive computation, and the Viewport component only re-renders when the iframe
   * itself is changes height or the user resizes the width of the Viewport.
   */
  React.useEffect(() => {
    const document = iframeRef.current?.contentDocument;
    if (document) {
      injectStyle(document);

      /*
       * The <body> element of the iframe document is the one which we observe and
       * measure. It should not have any margins (we remove them by injecting a simple
       * style reset into the iframe document).
       */
      iframeRO.observe(document.body);
    }
  });

  /*
   * Inject a simple style reset into the document.
   */
  function injectStyle(document: Document): void {
    if (document.querySelector("style#timvir-viewport-style")) {
      return;
    }
    const style = document.createElement("style");
    style.id = "timvir-viewport-style";
    style.innerHTML = "body { margin: 0 }";
    document.head.appendChild(style);
  }
  return <>
      <div ref={containerRef} />
      <Root ref={ref} {...rest} className={cx(className, fullWidth, "r1ouu0bc")}>
        <div ref={svgRef} className={"d1uj09ka"}>
          <Ruler containerWidth={svgROE?.contentRect.width} viewportWidth={width} />
          <div className={"d2uawyc"}>
            {width}px
          </div>
        </div>
        <div className={"d8ms9m9"}>
          <div>
            <div className={"d17q9rbm"}>
              <div className={cx("daubpa6", height === undefined && "d1d22s19")} style={{
              width,
              height
            }}>
                <iframe ref={iframeRef} frameBorder="0" src={src} onLoad={ev => {
                const document = ev.currentTarget.contentDocument;
                if (document) {
                  injectStyle(document);
                  const {
                    height
                  } = document.body.getBoundingClientRect();
                  setHeight(height);
                  setMaxHeight(height);
                }
              }} className={"i11uos8q"} style={{
                opacity: height === undefined ? 0 : 1,
                pointerEvents: height === undefined ? "none" : undefined
              }} />
              </div>

              <Handle gridColumn="1" lock={lock} edge="left" iframeRef={iframeRef} />
              <Handle gridColumn="3" lock={lock} edge="right" iframeRef={iframeRef} />
            </div>
          </div>
        </div>

        <Ruler containerWidth={svgROE?.contentRect.width} viewportWidth={width} />
      </Root>

      <Caption src={src} code={code} />

      <div style={{
      height: (maxHeight ?? 0) - (height ?? 0),
      transition: "height 0.16s"
    }} />
    </>;
}
export default React.forwardRef(Viewport);

require("./Viewport.linaria.module.css!=!../../../../node_modules/.pnpm/@linaria+webpack5-loader@5.0.4_webpack@5.91.0/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js?cacheProvider=!./Viewport.tsx");